// T043 + T046 + T047 — Wallet admin landing page.
//
// Top: paginated/sortable/filterable/searchable wallets table sourced from
// `GET /admin/wallets`. Bottom: manual lookup form by email or emaid (kept as
// a fallback for ops who already know the identifier or whose target wallet
// is hard to find). When the listing endpoint 404s (BE not yet deployed) the
// table degrades silently and only the manual form renders, with a small note.
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "primereact/button";
import { Card } from "primereact/card";
import { InputText } from "primereact/inputtext";
import { Message } from "primereact/message";
import { Toast } from "primereact/toast";

import useAdminWalletList from "../../hooks/useAdminWalletList";
import {
  looksLikeEmaid,
  resolveAccountFromEmail,
} from "../../services/wallet/accountLookup";
import { adminIssueCredit } from "../../services/wallet/adminWalletApi";
import type { AdminIssueRequest } from "../../types/wallet";
import { formatEur } from "../../utils/eur";

import IssueCreditDialog from "./components/IssueCreditDialog";
import WalletListTable from "./components/WalletListTable";
import WalletListToolbar from "./components/WalletListToolbar";

type ManualLookupState = "idle" | "searching" | "not_found" | "no_emaid" | "error";

const SEARCH_DEBOUNCE_MS = 300;

const WalletAdminPage = () => {
  const navigate = useNavigate();

  const list = useAdminWalletList();

  // T045 — local mirror of the search input that we debounce into the
  // hook's `query.search`. This keeps typing snappy without firing one
  // request per keystroke (FR-L05).
  // Destructure stable references so the effect doesn't re-run on every
  // render (the `list` object itself is a fresh reference each render).
  const { setQuery: setListQuery } = list;
  const currentSearch = list.query.search;
  const [searchInput, setSearchInput] = useState("");
  useEffect(() => {
    const handle = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      const next = trimmed.length > 0 ? trimmed : undefined;
      if (next !== currentSearch) {
        setListQuery({ search: next });
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [searchInput, currentSearch, setListQuery]);

  // Manual lookup state — preserves the prior behaviour as a fallback path.
  const [manualQuery, setManualQuery] = useState("");
  const [manualState, setManualState] = useState<ManualLookupState>("idle");

  // Top-level Issue Credit dialog — for accounts that have no wallet yet.
  const toast = useRef<Toast>(null);
  const [issueDialogOpen, setIssueDialogOpen] = useState(false);
  const [issueSubmitting, setIssueSubmitting] = useState(false);

  const handleIssueSubmit = useCallback(
    async ({ accountId, request }: { accountId: string; request: AdminIssueRequest }) => {
      setIssueSubmitting(true);
      try {
        const response = await adminIssueCredit(accountId, request);
        await list.refresh();
        if (response.idempotent_replay) {
          toast.current?.show({
            severity: "warn",
            summary: "Nothing new credited",
            detail: `This issuance was already processed, so no additional credits were issued to ${accountId}. Check the transaction history before issuing again.`,
            life: 6000,
          });
        } else {
          toast.current?.show({
            severity: "success",
            summary: "Credits issued",
            detail: `Issued ${formatEur(request.amount)} to ${accountId}.`,
            life: 4000,
          });
        }
        setIssueDialogOpen(false);
      } catch (err: any) {
        const detail =
          err?.response?.data?.message ??
          "Failed to issue credits. Check the form and try again.";
        toast.current?.show({
          severity: "error",
          summary: "Issuance failed",
          detail,
          life: 5000,
        });
      } finally {
        setIssueSubmitting(false);
      }
    },
    [list],
  );

  const handleManualSearch = async () => {
    const trimmed = manualQuery.trim();
    if (!trimmed) return;
    if (looksLikeEmaid(trimmed)) {
      navigate(`/wallet-admin/${encodeURIComponent(trimmed)}`);
      return;
    }
    setManualState("searching");
    try {
      const account = await resolveAccountFromEmail(trimmed);
      if (!account) {
        setManualState("not_found");
        return;
      }
      if (!account.emaid) {
        setManualState("no_emaid");
        return;
      }
      setManualState("idle");
      navigate(`/wallet-admin/${encodeURIComponent(account.emaid)}`);
    } catch (err) {
      console.error("Manual account lookup failed", err);
      setManualState("error");
    }
  };

  return (
    <div className="p-4 wallet-admin-page">
      <Toast ref={toast} />

      <div className="flex flex-column sm:flex-row sm:align-items-start sm:justify-content-between gap-3 mb-4">
        <div>
          <h2 className="m-0">Wallet Admin</h2>
          <p className="text-color-secondary mt-2 mb-0">
            Browse all wallets, or look up a specific account by email or emaid.
            Click any row to open its detail page where you can issue or revoke
            credits.
          </p>
        </div>
        <Button
          label="Issue credit"
          icon="pi pi-plus-circle"
          onClick={() => setIssueDialogOpen(true)}
        />
      </div>

      {/* Listing surface — User Story 1. Hidden when the endpoint is not yet
          deployed in this environment so the manual form takes over. */}
      {!list.endpointMissing && (
        <>
          <Message
            severity="info"
            className="w-full mb-3"
            text="Only accounts that have received credits at least once appear here. A wallet is created automatically the first time you issue credits to an account, so brand-new users won't show up until then. To issue credits to anyone — existing or not — use 'Issue credit' above."
          />

          <WalletListToolbar
            searchInput={searchInput}
            onSearchInputChange={setSearchInput}
            balance={list.query.balance ?? "any"}
            onBalanceChange={(next) => list.setQuery({ balance: next })}
            sort={list.query.sort ?? "last_activity_desc"}
            onSortChange={(next) => list.setQuery({ sort: next })}
          />

          <WalletListTable
            items={list.items}
            loading={list.loading}
            hasMore={list.hasMore}
            error={list.error}
            query={list.query}
            searchInput={searchInput}
            onLoadMore={list.loadMore}
            onRetry={list.refresh}
            onSortChange={(next) => list.setQuery({ sort: next })}
          />
        </>
      )}

      {list.endpointMissing && (
        <Message
          severity="info"
          className="w-full mb-4"
          text="Wallet listing endpoint is not yet available in this environment. Use the manual lookup below."
        />
      )}

      <Card className="mt-4">
        <div className="flex flex-column gap-3">
          <div>
            <h3 className="m-0">Don&apos;t see the wallet?</h3>
            <p className="text-color-secondary text-sm mt-1 mb-0">
              Look up a specific account by email or emaid. Useful when the
              search above doesn&apos;t match or when you already know the
              identifier.
            </p>
          </div>
          <div className="flex flex-column sm:flex-row gap-2">
            <InputText
              value={manualQuery}
              onChange={(event) => {
                setManualQuery(event.target.value);
                if (manualState !== "idle") setManualState("idle");
              }}
              onKeyDown={(event) => {
                if (event.key === "Enter") handleManualSearch();
              }}
              placeholder="user@example.com or DE-XYZ-12345-67 (emaid)"
              className="w-full sm:w-30rem"
            />
            <Button
              label="Open wallet"
              icon="pi pi-arrow-right"
              loading={manualState === "searching"}
              onClick={handleManualSearch}
              disabled={!manualQuery.trim()}
            />
          </div>

          {manualState === "not_found" && (
            <Message severity="warn" text="No CoCharge account found for that email." />
          )}
          {manualState === "no_emaid" && (
            <Message
              severity="warn"
              text="That account has no eMobility ID (emaid) and cannot have a wallet."
            />
          )}
          {manualState === "error" && (
            <Message
              severity="error"
              text="Lookup failed. Check the address and try again."
            />
          )}
        </div>
      </Card>

      <IssueCreditDialog
        visible={issueDialogOpen}
        loading={issueSubmitting}
        onSubmit={handleIssueSubmit}
        onHide={() => !issueSubmitting && setIssueDialogOpen(false)}
      />
    </div>
  );
};

export default WalletAdminPage;
